import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Undo2, Pencil, Trash2, Save } from 'lucide-react';
import { MarketPanel } from './MarketPanel';
import { MatchSearch } from './MatchSearch';
import { SaveDetails } from './SaveDetails';
import { MultiRow, SystemGroup, groupPicks } from './EditorRows';
import { amountsFrom, amountsPayload, withOdds } from './AmountsEditor';
import { SEL_API } from '../../lib/selectionShare';

const pad = (n) => String(n).padStart(2, '0');
const nowName = () => {
  const d = new Date();
  return `Selezione ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const eur = (n) => Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MATCH_KEYS = ['fixture_id', 'home_team', 'away_team', 'league_name', 'match_date', 'match_time'];

const Summary = ({ kind, picks, details }) => {
  const ok = picks.filter((p) => p.fixture_id);
  const matches = groupPicks(ok).length;
  const errors = Math.min(Number(details.errors) || 0, Math.max(ok.length - 1, 0));
  const a = details.amounts;
  return (
    <div className="mb-4" data-testid="editor-summary">
      <p className="text-white font-black text-xl uppercase leading-tight" style={{ fontFamily: 'Barlow Condensed' }} data-testid="editor-summary-title">
        {kind === 'sistema' ? 'Sistema' : 'Singola/Multipla'} – {matches} {matches === 1 ? 'partita' : 'partite'}
        {kind === 'sistema' ? ` · ${ok.length} eventi` : ''}
      </p>
      <p className="text-xs text-gray-400 mt-1" data-testid="editor-summary-amounts">
        Puntata <b className="text-white">{eur(a.stake)}</b> · Vincita <b className="text-white">{eur(a.potential)}</b> · Moltiplicatore <b className="text-[#F5A623]">{a.multiplier ? `${a.multiplier}x` : '—'}</b>
        {kind === 'sistema' && errors ? ` · ${errors} ${errors === 1 ? 'errore' : 'errori'}` : ''}
      </p>
    </div>
  );
};

const KindToggle = ({ kind, onChange }) => (
  <div className="flex gap-2 mb-4" data-testid="editor-kind">
    {[['multipla', 'Singola/Multipla'], ['sistema', 'Sistema']].map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`editor-kind-${k}`}
        className={`px-4 py-2 rounded-full text-sm font-black border transition-colors ${kind === k ? 'bg-[#F5A623] text-[#1A1A1A] border-[#F5A623]' : 'border-white/20 text-gray-300'}`}>{l}</button>
    ))}
  </div>
);

const BottomBar = ({ onUndo, canUndo, onEdit, onDelete, onSave }) => (
  <div className="fixed bottom-0 inset-x-0 z-[9985] pb-[max(env(safe-area-inset-bottom,0px),0.75rem)] pointer-events-none">
    <div className="max-w-md mx-auto px-4 flex items-center gap-3 pointer-events-auto">
      <div className="flex-1 h-14 rounded-full bg-[#1F1F1F] border border-white/10 flex items-center justify-around">
        <button type="button" onClick={onUndo} disabled={!canUndo} className="p-2 text-white disabled:opacity-30" aria-label="Annulla" data-testid="editor-undo"><Undo2 className="w-5 h-5" /></button>
        <button type="button" onClick={onEdit} className="p-2 text-white" aria-label="Modifica" data-testid="editor-edit"><Pencil className="w-5 h-5" /></button>
        <button type="button" onClick={onDelete} className="p-2 text-red-400" aria-label="Elimina" data-testid="editor-delete"><Trash2 className="w-5 h-5" /></button>
      </div>
      <button type="button" onClick={onSave} className="w-16 h-16 rounded-full bg-[#F5A623] text-[#1A1A1A] flex items-center justify-center shadow-[0_8px_24px_rgba(245,166,35,0.4)] active:scale-95 transition-transform" aria-label="Salva" data-testid="editor-save"><Save className="w-6 h-6" /></button>
    </div>
  </div>
);

// Pagina della selezione: Singola/Multipla or Sistema (Fisse + errori consentiti); new, imported or saved selection
export const SelectionEditor = ({ initial = {}, existing = null, source = 'manual', onClose, onSaved }) => {
  const base = existing || initial;
  const [doc, setDoc] = useState({ kind: base.kind === 'sistema' ? 'sistema' : 'multipla', picks: (base.picks || []).map(({ state, ...p }) => p) });
  const [hist, setHist] = useState([]);
  const [details, setDetails] = useState({
    name: base.description || nowName(), code: base.booking_code || '', errors: base.errors_allowed || 0,
    amounts: amountsFrom(base.stake, base.potential, base.odds),
  });
  const [search, setSearch] = useState(null);
  const [panel, setPanel] = useState(null);
  const [sheet, setSheet] = useState(base.openSave ? 'save' : null);
  const [saving, setSaving] = useState(false);
  const groups = useMemo(() => groupPicks(doc.picks), [doc.picks]);
  const { picks, kind } = doc;

  const commit = (next) => { setHist((h) => [...h.slice(-30), doc]); setDoc({ ...doc, ...next }); };
  const setPicks = (p) => commit({ picks: p });
  const undo = () => { if (!hist.length) return; setDoc(hist[hist.length - 1]); setHist((h) => h.slice(0, -1)); };

  const onPickMarket = (market) => {
    if (panel.index != null) setPicks(picks.map((p, i) => (i === panel.index ? { ...p, market, _odds: 0 } : p)));
    else {
      const same = picks.find((p) => p.fixture_id === panel.match.fixture_id);
      setPicks([...picks, { ...Object.fromEntries(MATCH_KEYS.map((k) => [k, panel.match[k]])), market, fixed: !!same?.fixed }]);
    }
    setPanel(null);
  };
  const onPickMatch = (m) => {
    const s = search;
    setSearch(null);
    if (s.index != null) setPicks(picks.map((p, i) => (i === s.index ? { ...p, ...Object.fromEntries(MATCH_KEYS.map((k) => [k, m[k]])), not_found: false, _odds: 0 } : p)));
    else setPanel({ match: m });
  };
  const open = (i) => (picks[i].fixture_id ? setPanel({ index: i }) : setSearch({ index: i, q: picks[i].home_team || '' }));
  const toggleFixed = (fid) => setPicks(picks.map((p) => (p.fixture_id === fid ? { ...p, fixed: !p.fixed } : p)));

  const remove = async () => {
    if (existing) {
      if (!window.confirm('Eliminare questa selezione?')) return;
      await axios.delete(`${SEL_API}/${existing.selection_id}`).then(() => { toast.success('Selezione eliminata'); onSaved?.(); onClose(); }).catch(() => toast.error('Eliminazione non riuscita'));
    } else if (picks.length && window.confirm('Svuotare la selezione?')) setPicks([]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        picks: picks.filter((p) => p.fixture_id), kind, errors_allowed: kind === 'sistema' ? Number(details.errors) || 0 : 0,
        booking_code: details.code, description: details.name, ...amountsPayload(details.amounts),
        ...(base.vip_prediction_id ? { vip_prediction_id: base.vip_prediction_id } : {}),
      };
      if (existing) {
        const { data } = await axios.put(`${SEL_API}/${existing.selection_id}`, body);
        toast.success('Selezione aggiornata');
        onSaved?.(data);
        onClose();
        return;
      }
      const { data } = await axios.post(SEL_API, { ...body, source });
      toast.success('Selezione salvata');
      onSaved?.(data, true);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Salvataggio non riuscito, riprova');
    } finally { setSaving(false); }
  };
  const askSave = () => {
    if (!picks.some((p) => p.fixture_id)) return toast.error('Aggiungi almeno una partita');
    if (details.amounts.odds !== undefined) {
      const same = picks.length === (base.picks || []).length && picks.every((p, i) => p.market === base.picks[i].market && p.fixture_id === base.picks[i].fixture_id);
      const product = picks.length && picks.every((p) => p._odds) ? Math.round(picks.reduce((m, p) => m * p._odds, 1) * 100) / 100 : 0;
      const odds = same ? base.odds : product;
      setDetails((d) => ({ ...d, amounts: withOdds(d.amounts, odds) }));
    }
    return setSheet('save');
  };

  return (
    <div className="fixed inset-0 z-[9980] bg-[#121212] overflow-y-auto" data-testid="selection-editor">
      <div className="max-w-md mx-auto px-4 pt-5 pb-36">
        <button type="button" onClick={onClose} className="mb-3 p-1 -ml-1 text-gray-300" aria-label="Indietro" data-testid="editor-close"><ArrowLeft className="w-6 h-6" /></button>
        <Summary kind={kind} picks={picks} details={details} />
        <KindToggle kind={kind} onChange={(k) => k !== kind && commit({ kind: k })} />
        <button type="button" onClick={() => setSearch({})} className="w-full py-4 rounded-2xl bg-[#F5A623] text-[#1A1A1A] font-black text-base flex items-center justify-center gap-2 mb-4 active:scale-[0.99] transition-transform" data-testid="editor-add-match">
          <Plus className="w-5 h-5" /> AGGIUNGI PARTITA
        </button>
        {picks.some((p) => !p.fixture_id) && <p className="text-[11px] text-red-400 mb-2" data-testid="editor-not-found-note">Le partite non trovate non verranno salvate: toccale per cercarle.</p>}
        {!picks.length && <p className="text-center text-gray-500 text-sm py-8" data-testid="editor-empty">Nessuna partita: tocca + AGGIUNGI PARTITA.</p>}
        <div className="divide-y divide-white/5" data-testid="editor-list">
          {kind === 'sistema' ? groups.map((g) => (
            <SystemGroup key={g.key} g={g} onOpenMatch={() => open(g.items[0].i)} onOpenBet={open}
              onRemoveBet={(i) => setPicks(picks.filter((_, j) => j !== i))}
              onRemoveMatch={() => setPicks(picks.filter((_, j) => !g.items.some((x) => x.i === j)))}
              onAddBet={() => setPanel({ match: g.first })} onToggleFixed={() => toggleFixed(g.first.fixture_id)} />
          )) : picks.map((p, i) => (
            <MultiRow key={`${p.fixture_id}-${i}`} p={p} i={i} onOpen={() => open(i)} onRemove={() => setPicks(picks.filter((_, j) => j !== i))} />
          ))}
        </div>
      </div>
      <BottomBar onUndo={undo} canUndo={hist.length > 0} onEdit={() => setSheet('edit')} onDelete={remove} onSave={askSave} />
      {search && <MatchSearch initial={search.q || ''} onClose={() => setSearch(null)} onPick={onPickMatch} />}
      {panel && <MarketPanel match={panel.match || picks[panel.index]} current={panel.index != null ? picks[panel.index].market : ''} onPick={onPickMarket} onClose={() => setPanel(null)} />}
      {sheet && (
        <SaveDetails value={details} onChange={setDetails} kind={kind} events={picks.filter((p) => p.fixture_id).length} mode={sheet}
          saving={saving} onConfirm={save} onClose={() => setSheet(null)} />
      )}
    </div>
  );
};

export default SelectionEditor;
